import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CheckCircle2, XCircle, Loader2, Building2,
  User, Phone, ArrowRight, Clock, Shield,
  Smartphone, Share, MoreVertical, PlusSquare
} from "lucide-react";
import PWAInstallPrompt from "@/components/time/PWAInstallPrompt";

const ROLE_LABELS = {
  admin: "Administrador",
  supervisor: "Supervisor",
  worker: "Treballador",
  auditor: "Auditor",
};

const ROLE_COLORS = {
  admin: "bg-purple-100 text-purple-700",
  supervisor: "bg-blue-100 text-blue-700",
  worker: "bg-emerald-100 text-emerald-700",
  auditor: "bg-gray-100 text-gray-600",
};

export default function WorkerInvite() {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");

  const [step, setStep] = useState("loading"); // loading | valid | form | success | error
  const [invitation, setInvitation] = useState(null);
  const [errorReason, setErrorReason] = useState("");
  const [form, setForm] = useState({ user_name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showPWA, setShowPWA] = useState(false);

  useEffect(() => {
    if (!token) { setStep("error"); setErrorReason("not_found"); return; }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    const res = await hosys.functions.invoke("inviteWorker", { action: "validate_token", token });
    const data = res.data;
    if (data.valid) {
      setInvitation(data.invitation);
      setForm({
        user_name: data.invitation.invited_name || "",
        phone: data.invitation.invited_phone || "",
      });
      setStep("valid");
    } else {
      setErrorReason(data.reason || "unknown");
      setStep("error");
    }
  };

  const handleAccept = async () => {
    if (!form.user_name.trim()) return;
    setSubmitting(true);
    const res = await hosys.functions.invoke("inviteWorker", {
      action: "accept",
      token,
      user_name: form.user_name,
      phone: form.phone,
    });
    if (res.data?.ok) {
      // Invite to platform (so they can log in)
      try {
        await hosys.users.inviteUser(invitation.invited_email, "user");
      } catch (err) { console.warn("[WorkerInvite] Platform invite failed:", err.message); }
      setStep("success");
    } else {
      setErrorReason(res.data?.error || "unknown");
      setStep("error");
    }
    setSubmitting(false);
  };

  const errorMessages = {
    not_found: "Aquesta invitació no existeix.",
    already_accepted: "Aquesta invitació ja ha estat acceptada.",
    cancelled: "Aquesta invitació ha estat cancel·lada.",
    expired: "Aquesta invitació ha caducat. Demana una nova invitació al teu responsable.",
    unknown: "S'ha produït un error. Torna a provar o contacta amb el teu responsable.",
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7B2D3F] to-[#9B4D5F] shadow-lg mb-3">
            <span className="text-white font-black text-xs tracking-widest">KIT</span>
          </div>
          <p className="text-gray-500 text-sm">Sistema de gestió Ho-Sys</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">

          {/* LOADING */}
          {step === "loading" && (
            <div className="p-10 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#7B2D3F] mx-auto mb-3" />
              <p className="text-gray-500">Validant invitació...</p>
            </div>
          )}

          {/* ERROR */}
          {step === "error" && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Invitació no vàlida</h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                {errorMessages[errorReason] || errorMessages.unknown}
              </p>
              <p className="text-xs text-gray-400 mt-4">
                Si creus que és un error, contacta amb el teu responsable.
              </p>
            </div>
          )}

          {/* VALID — Show invite info + form */}
          {(step === "valid") && invitation && (
            <>
              {/* Header */}
              <div className="bg-gradient-to-br from-[#7B2D3F] to-[#9B4D5F] p-6 text-white text-center">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Building2 className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-xl font-bold">{invitation.venue_name}</h1>
                {invitation.venue_city && (
                  <p className="text-white/70 text-sm mt-0.5">{invitation.venue_city}</p>
                )}
              </div>

              <div className="p-6 space-y-5">
                <div>
                  <p className="text-gray-600 text-sm">
                    <strong>{invitation.invited_by_name}</strong> t'ha convidat a unir-te a l'equip de{" "}
                    <strong>{invitation.venue_name}</strong>.
                  </p>
                </div>

                {/* Role badge */}
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                  <Shield className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400">Rol assignat</p>
                    <span className={`text-sm font-semibold px-2 py-0.5 rounded-full ${ROLE_COLORS[invitation.role]}`}>
                      {ROLE_LABELS[invitation.role] || invitation.role}
                    </span>
                  </div>
                  {invitation.position && (
                    <>
                      <div className="w-px h-8 bg-gray-200" />
                      <div>
                        <p className="text-xs text-gray-400">Posició</p>
                        <p className="text-sm font-medium text-gray-700">{invitation.position}</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Expiry notice */}
                <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
                  <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                  Caducat el {new Date(invitation.expires_at).toLocaleDateString("ca-ES", { day: "numeric", month: "long", year: "numeric" })}
                </div>

                {/* Form */}
                <div className="space-y-3 pt-1">
                  <div>
                    <Label className="flex items-center gap-1.5 mb-1">
                      <User className="w-3.5 h-3.5 text-gray-400" /> Nom complet *
                    </Label>
                    <Input
                      value={form.user_name}
                      onChange={e => setForm(f => ({ ...f, user_name: e.target.value }))}
                      placeholder="Joan García"
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5 mb-1">
                      <Phone className="w-3.5 h-3.5 text-gray-400" /> Telèfon (opcional)
                    </Label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="6XX XXX XXX"
                      className="rounded-xl"
                    />
                  </div>

                  <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                    <p className="text-xs text-blue-700 font-medium">Email d'accés</p>
                    <p className="text-sm text-blue-900 font-semibold mt-0.5">{invitation.invited_email}</p>
                    <p className="text-xs text-blue-600 mt-1">Rebràs un email per accedir al sistema.</p>
                  </div>
                </div>

                <Button
                  onClick={handleAccept}
                  disabled={submitting || !form.user_name.trim()}
                  className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white rounded-xl h-12 font-bold text-base"
                >
                  {submitting
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processant...</>
                    : <>Acceptar i unir-me <ArrowRight className="w-4 h-4 ml-1" /></>
                  }
                </Button>

                <p className="text-xs text-gray-400 text-center">
                  En acceptar, confirmes que has llegit i acceptes els termes d'ús de Ho-Sys.
                </p>
              </div>
            </>
          )}

          {/* SUCCESS */}
          {step === "success" && (
            <div className="p-6">
              {/* Top confirmation */}
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">T'has unit!</h2>
                <p className="text-gray-500 text-sm">
                  Benvingut/da a <strong>{invitation?.venue_name}</strong>.
                </p>
              </div>

              {/* Status steps */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 mb-5">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  Perfil creat correctament
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  Assignat a {invitation?.venue_name}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  Email d'accés enviat a {invitation?.invited_email}
                </div>
              </div>

              {/* PWA Install CTA */}
              <div className="bg-gradient-to-br from-[#7B2D3F] to-[#9B4D5F] rounded-2xl p-5 text-white mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-base">Instal·la Ho-Sys al mòbil</p>
                    <p className="text-white/70 text-xs mt-0.5">Fitxa en 2 tocs, sense entrar al navegador</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPWA(true)}
                  className="w-full bg-white text-[#7B2D3F] font-bold rounded-xl py-3 text-sm transition-all active:scale-95 shadow-md"
                >
                  Com instal·lar-la →
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Comprova el teu correu per activar el compte i accedir.
              </p>
            </div>
          )}

          {showPWA && (
            <PWAInstallPrompt onClose={() => setShowPWA(false)} />
          )}
        </div>

        <p className="text-xs text-gray-400 text-center mt-4">
          Ho-Sys · Sistema de gestió per a restaurants · RD-Llei 8/2019
        </p>
      </div>
    </div>
  );
}